import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { AvailabilityManager } from '../components/AvailabilityManager';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Clock, 
  Building2, 
  Gamepad2,
  RefreshCw,
  Save,
  ChevronDown,
  ChevronUp,
  Sun,
  Moon,
  AlertCircle
} from 'lucide-react';

import { BACKEND_URL, API } from '../utils/apiConfig';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Generate time options (30-min intervals in 24h format)
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    const displayHour = h % 12 || 12;
    const period = h < 12 ? 'AM' : 'PM';
    const display = `${displayHour}:${m.toString().padStart(2, '0')} ${period}`;
    TIME_OPTIONS.push({ value: time, label: display });
  }
}

// Default schedule for a week
const DEFAULT_SCHEDULE = DAY_NAMES.map((_, idx) => ({
  day_of_week: idx,
  is_open: true,
  open_time: '06:00',
  close_time: '22:00'
}));

export default function WorkingHoursPage() {
  const { admin } = useAuth();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedVenues, setExpandedVenues] = useState([]);
  
  // Court schedule editing
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCourt, setEditingCourt] = useState(null);
  const [editSchedule, setEditSchedule] = useState([...DEFAULT_SCHEDULE]);
  
  // Quick edit mode
  const [quickEditMode, setQuickEditMode] = useState(false);

  const isAdmin = admin?.role === 'admin' || admin?.email === 'admin@maks.com';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const venuesRes = await axios.get(`${API}/masters/venues-with-courts`);
      const venuesData = venuesRes.data.venues || [];
      
      // Fetch working hours for all courts
      const workingHoursRes = await axios.get(`${API}/masters/court-working-hours`);
      const allHours = workingHoursRes.data.working_hours || [];
      
      // Group hours by court
      const hoursByCourtId = {};
      allHours.forEach(h => {
        if (!hoursByCourtId[h.court_id]) {
          hoursByCourtId[h.court_id] = [];
        }
        hoursByCourtId[h.court_id].push(h);
      });
      
      // Enrich venues with working hours
      const enrichedVenues = venuesData.map(venue => ({
        ...venue,
        courts: (venue.courts || []).map(court => ({
          ...court,
          working_hours: hoursByCourtId[court.id] || []
        }))
      }));
      
      setVenues(enrichedVenues);
      
      // Auto-expand first venue
      if (enrichedVenues.length > 0 && expandedVenues.length === 0) {
        setExpandedVenues([enrichedVenues[0].id]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleVenue = (venueId) => {
    setExpandedVenues(prev => 
      prev.includes(venueId)
        ? prev.filter(id => id !== venueId)
        : [...prev, venueId]
    );
  };

  const getScheduleForDay = (workingHours, dayOfWeek) => {
    const found = workingHours.find(h => h.day_of_week === dayOfWeek);
    if (found) {
      return {
        is_open: found.is_open,
        open_time: found.open_time || '06:00',
        close_time: found.close_time || '22:00'
      };
    }
    // Default
    return { is_open: true, open_time: '06:00', close_time: '22:00' };
  };

  const formatTimeRange = (openTime, closeTime) => {
    const formatTime = (t) => {
      const [h, m] = t.split(':').map(Number);
      const displayH = h % 12 || 12;
      const period = h < 12 ? 'AM' : 'PM';
      return `${displayH}:${m.toString().padStart(2, '0')} ${period}`;
    };
    return `${formatTime(openTime)} - ${formatTime(closeTime)}`;
  };

  const openEditModal = (court, venue) => {
    setEditingCourt({ ...court, venue_name: venue.name });
    
    // Build schedule from existing hours or defaults
    const schedule = DAY_NAMES.map((_, idx) => {
      const existing = court.working_hours.find(h => h.day_of_week === idx);
      if (existing) {
        return {
          day_of_week: idx,
          is_open: existing.is_open,
          open_time: existing.open_time || '06:00',
          close_time: existing.close_time || '22:00'
        };
      }
      return {
        day_of_week: idx,
        is_open: true,
        open_time: '06:00',
        close_time: '22:00'
      };
    });
    
    setEditSchedule(schedule);
    setEditModalOpen(true);
  };

  const updateScheduleDay = (dayIdx, field, value) => {
    setEditSchedule(prev => prev.map((day, idx) => 
      idx === dayIdx ? { ...day, [field]: value } : day
    ));
  };

  const applyToAllDays = (dayIdx) => {
    const source = editSchedule[dayIdx];
    setEditSchedule(prev => prev.map(day => ({
      ...day,
      is_open: source.is_open,
      open_time: source.open_time,
      close_time: source.close_time
    })));
    toast.success('Applied to all days');
  };

  const applyToWeekdays = (dayIdx) => {
    const source = editSchedule[dayIdx];
    setEditSchedule(prev => prev.map((day, idx) => 
      idx < 5 ? { ...day, is_open: source.is_open, open_time: source.open_time, close_time: source.close_time } : day
    ));
    toast.success('Applied to weekdays (Mon-Fri)');
  };

  const applyToWeekends = (dayIdx) => {
    const source = editSchedule[dayIdx];
    setEditSchedule(prev => prev.map((day, idx) => 
      idx >= 5 ? { ...day, is_open: source.is_open, open_time: source.open_time, close_time: source.close_time } : day
    ));
    toast.success('Applied to weekends (Sat-Sun)');
  };

  const saveSchedule = async () => {
    if (!editingCourt) return;
    
    setSaving(true);
    try {
      const payload = {
        court_id: editingCourt.id,
        hours: editSchedule.map(s => ({
          court_id: editingCourt.id,
          day_of_week: s.day_of_week,
          is_open: s.is_open,
          open_time: s.open_time,
          close_time: s.close_time
        }))
      };
      
      await axios.post(`${API}/masters/court-working-hours/bulk`, payload);
      toast.success(`Working hours saved for ${editingCourt.name}`);
      setEditModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error(error.response?.data?.detail || 'Failed to save working hours');
    } finally {
      setSaving(false);
    }
  };

  const getOpenDaysCount = (workingHours) => {
    if (workingHours.length === 0) return 7; // Default all open
    return workingHours.filter(h => h.is_open).length;
  };

  const hasCustomHours = (workingHours) => {
    if (workingHours.length === 0) return false;
    return workingHours.some(h => 
      h.open_time !== '06:00' || h.close_time !== '22:00' || !h.is_open
    );
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Clock className="w-7 h-7 text-blue-600" />
              Court Working Hours
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Configure opening/closing times for each court by day of week
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-hours"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Info Banner */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 font-medium">How Working Hours Affect Bookings</p>
              <p className="text-sm text-blue-700 mt-1">
                Slots outside configured working hours will be marked as unavailable in the customer booking portal.
                If all courts are closed on a day, customers will see "All courts are closed on this day".
              </p>
            </div>
          </div>
        </Card>

        {/* Venues & Courts */}
        <Tabs defaultValue="weekly" className="w-full">
          <TabsList data-testid="availability-tabs">
            <TabsTrigger value="weekly" data-testid="tab-weekly">Weekly Schedule</TabsTrigger>
            <TabsTrigger value="recurring" data-testid="tab-recurring">Recurring Rules</TabsTrigger>
            <TabsTrigger value="overrides" data-testid="tab-overrides">Date Overrides</TabsTrigger>
          </TabsList>
          <TabsContent value="weekly" className="mt-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : venues.length === 0 ? (
          <Card className="p-12 text-center">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Venues Found</h3>
            <p className="text-slate-500">Create venues and courts first in the Venue & Court Master page.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {venues.map(venue => (
              <Card key={venue.id} className="overflow-hidden">
                {/* Venue Header */}
                <button
                  onClick={() => toggleVenue(venue.id)}
                  className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-slate-900">{venue.name}</p>
                      <p className="text-sm text-slate-500">
                        {venue.courts?.length || 0} court{(venue.courts?.length || 0) !== 1 ? 's' : ''}
                        {venue.location && ` • ${venue.location}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {expandedVenues.includes(venue.id) ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </button>
                
                {/* Courts List */}
                {expandedVenues.includes(venue.id) && (
                  <div className="p-4 space-y-3">
                    {(venue.courts || []).length === 0 ? (
                      <p className="text-center text-slate-500 py-4">No courts in this venue</p>
                    ) : (
                      venue.courts.map(court => {
                        const openDays = getOpenDaysCount(court.working_hours);
                        const hasCustom = hasCustomHours(court.working_hours);
                        
                        return (
                          <div 
                            key={court.id}
                            className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200 hover:border-blue-200 transition-colors"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                                <Gamepad2 className="w-5 h-5 text-emerald-600" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{court.name}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    openDays === 7 
                                      ? 'bg-emerald-100 text-emerald-700' 
                                      : openDays === 0 
                                        ? 'bg-red-100 text-red-700'
                                        : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    {openDays === 7 ? 'Open all days' : 
                                     openDays === 0 ? 'Closed all days' :
                                     `Open ${openDays}/7 days`}
                                  </span>
                                  {hasCustom && (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                      Custom hours
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              {/* Mini Schedule Preview */}
                              <div className="hidden md:flex gap-1">
                                {DAY_SHORT.map((day, idx) => {
                                  const schedule = getScheduleForDay(court.working_hours, idx);
                                  return (
                                    <div 
                                      key={day}
                                      className={`w-8 h-8 rounded flex items-center justify-center text-xs font-medium ${
                                        schedule.is_open 
                                          ? 'bg-emerald-100 text-emerald-700' 
                                          : 'bg-slate-200 text-slate-400'
                                      }`}
                                      title={`${DAY_NAMES[idx]}: ${schedule.is_open ? formatTimeRange(schedule.open_time, schedule.close_time) : 'Closed'}`}
                                    >
                                      {day.charAt(0)}
                                    </div>
                                  );
                                })}
                              </div>
                              
                              {isAdmin && (
                                <Button
                                  size="sm"
                                  onClick={() => openEditModal(court, venue)}
                                  data-testid={`edit-hours-${court.id}`}
                                >
                                  <Clock className="w-4 h-4 mr-1" />
                                  Edit Hours
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
          </TabsContent>
          <TabsContent value="recurring" className="mt-4">
            <AvailabilityManager mode="recurring" venues={venues} />
          </TabsContent>
          <TabsContent value="overrides" className="mt-4">
            <AvailabilityManager mode="overrides" venues={venues} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Working Hours Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Working Hours - {editingCourt?.name}
              <span className="text-sm font-normal text-slate-500">
                ({editingCourt?.venue_name})
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2 pb-3 border-b">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditSchedule(DEFAULT_SCHEDULE.map(s => ({ ...s })))}
              >
                Reset to Default (6AM-10PM)
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditSchedule(prev => prev.map(s => ({ ...s, is_open: true })))}
              >
                Open All Days
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditSchedule(prev => prev.map((s, idx) => ({ ...s, is_open: idx < 5 })))}
              >
                Weekdays Only
              </Button>
            </div>
            
            {/* Schedule Grid */}
            <div className="space-y-3">
              {editSchedule.map((day, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${
                    day.is_open ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  {/* Day Name */}
                  <div className="w-24 flex items-center gap-2">
                    {idx >= 5 ? (
                      <Sun className="w-4 h-4 text-amber-500" />
                    ) : (
                      <Moon className="w-4 h-4 text-slate-400" />
                    )}
                    <span className="font-medium text-slate-700">{DAY_NAMES[idx]}</span>
                  </div>
                  
                  {/* Open/Closed Toggle */}
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={day.is_open}
                      onCheckedChange={(checked) => updateScheduleDay(idx, 'is_open', checked)}
                      data-testid={`toggle-day-${idx}`}
                    />
                    <span className={`text-sm ${day.is_open ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {day.is_open ? 'Open' : 'Closed'}
                    </span>
                  </div>
                  
                  {/* Time Selectors */}
                  {day.is_open && (
                    <>
                      <div className="flex items-center gap-2">
                        <Select 
                          value={day.open_time} 
                          onValueChange={(val) => updateScheduleDay(idx, 'open_time', val)}
                        >
                          <SelectTrigger className="w-[120px]" data-testid={`open-time-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {TIME_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-slate-400">to</span>
                        <Select 
                          value={day.close_time} 
                          onValueChange={(val) => updateScheduleDay(idx, 'close_time', val)}
                        >
                          <SelectTrigger className="w-[120px]" data-testid={`close-time-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {TIME_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Apply Actions */}
                      <div className="ml-auto flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => applyToAllDays(idx)}
                          title="Apply to all days"
                        >
                          All
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => applyToWeekdays(idx)}
                          title="Apply to Mon-Fri"
                        >
                          Wkdays
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => applyToWeekends(idx)}
                          title="Apply to Sat-Sun"
                        >
                          Wkend
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveSchedule}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-hours-btn"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1" />
                  Save Hours
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
